import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import { applyOptimisticLike, patchMarketInList } from "../utils/bondPredictHelpers";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

export const fetchBondPredictMarkets = createAsyncThunk(
  "bondPredict/fetchMarkets",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/polymarket-v2/markets", { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load markets"));
    }
  }
);

export const fetchBondPredictTrending = createAsyncThunk(
  "bondPredict/fetchTrending",
  async (limit = 8, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/polymarket-v2/markets/trending", {
        params: { limit },
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load trending markets"));
    }
  }
);

export const fetchBondPredictLeagues = createAsyncThunk(
  "bondPredict/fetchLeagues",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/polymarket-v2/markets/leagues");
      return res.data?.leagues || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load leagues"));
    }
  }
);

export const toggleBondPredictLike = createAsyncThunk(
  "bondPredict/toggleLike",
  async ({ marketId, isLiked, userId }, { rejectWithValue }) => {
    if (!userId) {
      return rejectWithValue("Sign in to like markets");
    }
    try {
      const path = isLiked ? "unlike" : "like";
      const res = await api.post(`/api/polymarket-v2/markets/${marketId}/${path}`);
      const market = res.data?.market || res.data;
      if (!market?._id) {
        return rejectWithValue("Invalid like response from server");
      }
      return market;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not update like"));
    }
  }
);

export const fetchBondPredictMarketById = createAsyncThunk(
  "bondPredict/fetchMarketById",
  async (marketId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/polymarket-v2/markets/${marketId}`);
      const market = res.data?.market;
      if (!market?._id) {
        return rejectWithValue("Market not found");
      }
      return market;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load market"));
    }
  }
);

export const fetchBondPredictOrderBook = createAsyncThunk(
  "bondPredict/fetchOrderBook",
  async ({ marketId, outcomeId }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/polymarket-v2/markets/${marketId}/outcomes/${outcomeId}/orderbook`
      );
      return {
        marketId: String(marketId),
        outcomeId: String(outcomeId),
        orderBook: res.data?.orderBook || res.data,
      };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load order book"));
    }
  }
);

export const fetchBondPredictMarketTrades = createAsyncThunk(
  "bondPredict/fetchMarketTrades",
  async ({ marketId, outcomeId, limit = 500 }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/polymarket-v2/markets/${marketId}/trades`, {
        params: { outcomeId, limit },
      });
      return {
        key: `${marketId}:${outcomeId}`,
        trades: res.data?.trades || [],
      };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load trade history"));
    }
  }
);

export const fetchBondPredictMarketStats = createAsyncThunk(
  "bondPredict/fetchMarketStats",
  async (marketId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/polymarket-v2/markets/${marketId}/stats`);
      return res.data?.stats || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load market stats"));
    }
  }
);

export const fetchBondPredictRelatedMarkets = createAsyncThunk(
  "bondPredict/fetchRelatedMarkets",
  async ({ category, excludeId, limit = 6 }, { rejectWithValue }) => {
    if (!category) return [];
    try {
      const res = await api.get("/api/polymarket-v2/markets", {
        params: { category, limit: limit + 1, sortBy: "metrics.totalVolume", sortOrder: "desc" },
      });
      const markets = (res.data?.markets || []).filter(
        (m) => m?._id && String(m._id) !== String(excludeId)
      );
      return markets.slice(0, limit);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load related markets"));
    }
  }
);

export const fetchBondPredictPortfolio = createAsyncThunk(
  "bondPredict/fetchPortfolio",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/polymarket-v2/portfolio", { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load portfolio"));
    }
  }
);

export const fetchBondPredictOrders = createAsyncThunk(
  "bondPredict/fetchOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/polymarket-v2/orders", { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load orders"));
    }
  }
);

export const cancelBondPredictOrder = createAsyncThunk(
  "bondPredict/cancelOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/polymarket-v2/orders/${orderId}/cancel`);
      return { orderId: String(orderId), ...res.data };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel order"));
    }
  }
);

export const placeBondPredictOrder = createAsyncThunk(
  "bondPredict/placeOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/polymarket-v2/orders", orderData);
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not place order"));
    }
  }
);

const initialState = {
  markets: [],
  trending: [],
  leagues: [],
  pagination: null,
  marketsStatus: "idle",
  trendingStatus: "idle",
  leaguesStatus: "idle",
  marketsError: null,
  trendingError: null,
  leaguesError: null,
  likeBusyId: null,
  likeSnapshots: {},
  currentMarket: null,
  currentMarketStatus: "idle",
  currentMarketError: null,
  orderBook: null,
  orderBookKey: null,
  orderBookStatus: "idle",
  orderBookError: null,
  marketTrades: [],
  marketTradesKey: null,
  marketTradesStatus: "idle",
  marketStats: null,
  marketStatsStatus: "idle",
  relatedMarkets: [],
  relatedMarketsStatus: "idle",
  portfolioSummary: null,
  portfolioPositions: [],
  portfolioPagination: null,
  portfolioStatus: "idle",
  portfolioError: null,
  portfolioOrders: [],
  portfolioOrdersPagination: null,
  portfolioOrdersStatus: "idle",
  portfolioOrdersError: null,
  cancelOrderBusyId: null,
  placeOrderStatus: "idle",
  placeOrderError: null,
};

const bondPredictSlice = createSlice({
  name: "bondPredict",
  initialState,
  reducers: {
    clearBondPredictErrors: (state) => {
      state.marketsError = null;
      state.trendingError = null;
    },
    clearBondPredictCurrentMarket: (state) => {
      state.currentMarket = null;
      state.currentMarketStatus = "idle";
      state.currentMarketError = null;
      state.orderBook = null;
      state.orderBookKey = null;
      state.orderBookStatus = "idle";
      state.orderBookError = null;
      state.marketTrades = [];
      state.marketTradesKey = null;
      state.marketTradesStatus = "idle";
      state.marketStats = null;
      state.marketStatsStatus = "idle";
      state.relatedMarkets = [];
      state.relatedMarketsStatus = "idle";
    },
    clearBondPredictPortfolio: (state) => {
      state.portfolioSummary = null;
      state.portfolioPositions = [];
      state.portfolioPagination = null;
      state.portfolioStatus = "idle";
      state.portfolioError = null;
      state.portfolioOrders = [];
      state.portfolioOrdersPagination = null;
      state.portfolioOrdersStatus = "idle";
      state.portfolioOrdersError = null;
      state.cancelOrderBusyId = null;
    },
    resetBondPredictPlaceOrder: (state) => {
      state.placeOrderStatus = "idle";
      state.placeOrderError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBondPredictMarkets.pending, (state) => {
        state.marketsStatus = "loading";
        state.marketsError = null;
      })
      .addCase(fetchBondPredictMarkets.fulfilled, (state, action) => {
        state.marketsStatus = "succeeded";
        state.markets = action.payload?.markets || [];
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchBondPredictMarkets.rejected, (state, action) => {
        state.marketsStatus = "failed";
        state.marketsError = action.payload || action.error?.message;
      })

      .addCase(fetchBondPredictTrending.pending, (state) => {
        state.trendingStatus = "loading";
        state.trendingError = null;
      })
      .addCase(fetchBondPredictTrending.fulfilled, (state, action) => {
        state.trendingStatus = "succeeded";
        state.trending = action.payload?.markets || [];
      })
      .addCase(fetchBondPredictTrending.rejected, (state, action) => {
        state.trendingStatus = "failed";
        state.trendingError = action.payload || action.error?.message;
      })

      .addCase(fetchBondPredictLeagues.pending, (state) => {
        state.leaguesStatus = "loading";
        state.leaguesError = null;
      })
      .addCase(fetchBondPredictLeagues.fulfilled, (state, action) => {
        state.leaguesStatus = "succeeded";
        state.leagues = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchBondPredictLeagues.rejected, (state, action) => {
        state.leaguesStatus = "failed";
        state.leaguesError = action.payload || action.error?.message;
      })

      .addCase(toggleBondPredictLike.pending, (state, action) => {
        const { marketId, isLiked, userId } = action.meta.arg;
        state.likeBusyId = marketId;
        const id = String(marketId);

        const snapshotFromList = (list) => {
          const found = list.find((m) => String(m._id) === id);
          if (found && !state.likeSnapshots[id]) {
            state.likeSnapshots[id] = found.likes
              ? { count: found.likes.count || 0, users: [...(found.likes.users || [])] }
              : { count: 0, users: [] };
          }
        };

        snapshotFromList(state.markets);
        snapshotFromList(state.trending);
        if (state.currentMarket && String(state.currentMarket._id) === id) {
          if (!state.likeSnapshots[id]) {
            state.likeSnapshots[id] = state.currentMarket.likes
              ? {
                  count: state.currentMarket.likes.count || 0,
                  users: [...(state.currentMarket.likes.users || [])],
                }
              : { count: 0, users: [] };
          }
          state.currentMarket = applyOptimisticLike(state.currentMarket, userId, isLiked);
        }

        state.markets = state.markets.map((m) =>
          String(m._id) === id ? applyOptimisticLike(m, userId, isLiked) : m
        );
        state.trending = state.trending.map((m) =>
          String(m._id) === id ? applyOptimisticLike(m, userId, isLiked) : m
        );
      })
      .addCase(toggleBondPredictLike.fulfilled, (state, action) => {
        state.likeBusyId = null;
        const updated = action.payload;
        if (!updated?._id) return;
        const id = String(updated._id);
        delete state.likeSnapshots[id];
        state.markets = patchMarketInList(state.markets, updated);
        state.trending = patchMarketInList(state.trending, updated);
        if (state.currentMarket && String(state.currentMarket._id) === id) {
          state.currentMarket = { ...state.currentMarket, ...updated };
        }
      })
      .addCase(toggleBondPredictLike.rejected, (state, action) => {
        const marketId = action.meta.arg?.marketId;
        state.likeBusyId = null;
        if (!marketId) return;
        const id = String(marketId);
        const snapshot = state.likeSnapshots[id];
        if (!snapshot) return;
        const restore = (m) =>
          String(m._id) === id ? { ...m, likes: snapshot } : m;
        state.markets = state.markets.map(restore);
        state.trending = state.trending.map(restore);
        if (state.currentMarket && String(state.currentMarket._id) === id) {
          state.currentMarket = { ...state.currentMarket, likes: snapshot };
        }
        delete state.likeSnapshots[id];
      })

      .addCase(fetchBondPredictMarketById.pending, (state) => {
        state.currentMarketStatus = "loading";
        state.currentMarketError = null;
      })
      .addCase(fetchBondPredictMarketById.fulfilled, (state, action) => {
        state.currentMarketStatus = "succeeded";
        state.currentMarket = action.payload;
      })
      .addCase(fetchBondPredictMarketById.rejected, (state, action) => {
        state.currentMarketStatus = "failed";
        state.currentMarketError = action.payload || action.error?.message;
        state.currentMarket = null;
      })

      .addCase(fetchBondPredictOrderBook.pending, (state) => {
        state.orderBookStatus = "loading";
        state.orderBookError = null;
      })
      .addCase(fetchBondPredictOrderBook.fulfilled, (state, action) => {
        state.orderBookStatus = "succeeded";
        state.orderBook = action.payload.orderBook;
        state.orderBookKey = `${action.payload.marketId}:${action.payload.outcomeId}`;
      })
      .addCase(fetchBondPredictOrderBook.rejected, (state, action) => {
        state.orderBookStatus = "failed";
        state.orderBookError = action.payload || action.error?.message;
        state.orderBook = null;
      })

      .addCase(fetchBondPredictMarketTrades.pending, (state, action) => {
        const { marketId, outcomeId } = action.meta.arg;
        const key = `${marketId}:${outcomeId}`;
        // Keep stale points visible while refreshing the same outcome.
        if (state.marketTradesKey !== key) state.marketTrades = [];
        state.marketTradesStatus = "loading";
      })
      .addCase(fetchBondPredictMarketTrades.fulfilled, (state, action) => {
        state.marketTradesStatus = "succeeded";
        state.marketTrades = action.payload.trades;
        state.marketTradesKey = action.payload.key;
      })
      .addCase(fetchBondPredictMarketTrades.rejected, (state) => {
        state.marketTradesStatus = "failed";
      })

      .addCase(fetchBondPredictMarketStats.pending, (state) => {
        state.marketStatsStatus = "loading";
      })
      .addCase(fetchBondPredictMarketStats.fulfilled, (state, action) => {
        state.marketStatsStatus = "succeeded";
        state.marketStats = action.payload;
      })
      .addCase(fetchBondPredictMarketStats.rejected, (state) => {
        state.marketStatsStatus = "failed";
        state.marketStats = null;
      })

      .addCase(fetchBondPredictRelatedMarkets.pending, (state) => {
        state.relatedMarketsStatus = "loading";
      })
      .addCase(fetchBondPredictRelatedMarkets.fulfilled, (state, action) => {
        state.relatedMarketsStatus = "succeeded";
        state.relatedMarkets = action.payload || [];
      })
      .addCase(fetchBondPredictRelatedMarkets.rejected, (state) => {
        state.relatedMarketsStatus = "failed";
        state.relatedMarkets = [];
      })

      .addCase(fetchBondPredictPortfolio.pending, (state) => {
        state.portfolioStatus = "loading";
        state.portfolioError = null;
      })
      .addCase(fetchBondPredictPortfolio.fulfilled, (state, action) => {
        state.portfolioStatus = "succeeded";
        state.portfolioSummary = action.payload?.summary || null;
        state.portfolioPositions = action.payload?.positions || [];
        state.portfolioPagination = action.payload?.pagination || null;
      })
      .addCase(fetchBondPredictPortfolio.rejected, (state, action) => {
        state.portfolioStatus = "failed";
        state.portfolioError = action.payload || action.error?.message;
      })

      .addCase(fetchBondPredictOrders.pending, (state) => {
        state.portfolioOrdersStatus = "loading";
        state.portfolioOrdersError = null;
      })
      .addCase(fetchBondPredictOrders.fulfilled, (state, action) => {
        state.portfolioOrdersStatus = "succeeded";
        state.portfolioOrders = action.payload?.orders || [];
        state.portfolioOrdersPagination = action.payload?.pagination || null;
      })
      .addCase(fetchBondPredictOrders.rejected, (state, action) => {
        state.portfolioOrdersStatus = "failed";
        state.portfolioOrdersError = action.payload || action.error?.message;
      })

      .addCase(cancelBondPredictOrder.pending, (state, action) => {
        state.cancelOrderBusyId = String(action.meta.arg);
      })
      .addCase(cancelBondPredictOrder.fulfilled, (state, action) => {
        state.cancelOrderBusyId = null;
        const orderId = String(action.payload?.orderId || action.meta.arg);
        const updated = action.payload?.order;
        state.portfolioOrders = state.portfolioOrders.map((o) => {
          if (String(o._id) !== orderId) return o;
          if (!updated) return { ...o, status: "CANCELLED" };
          return {
            ...o,
            ...updated,
            market:
              updated.market && typeof updated.market === "object"
                ? { ...(typeof o.market === "object" ? o.market : {}), ...updated.market }
                : o.market,
            outcome:
              updated.outcome && typeof updated.outcome === "object"
                ? { ...(typeof o.outcome === "object" ? o.outcome : {}), ...updated.outcome }
                : o.outcome,
          };
        });
      })
      .addCase(cancelBondPredictOrder.rejected, (state) => {
        state.cancelOrderBusyId = null;
      })

      .addCase(placeBondPredictOrder.pending, (state) => {
        state.placeOrderStatus = "loading";
        state.placeOrderError = null;
      })
      .addCase(placeBondPredictOrder.fulfilled, (state) => {
        state.placeOrderStatus = "succeeded";
      })
      .addCase(placeBondPredictOrder.rejected, (state, action) => {
        state.placeOrderStatus = "failed";
        state.placeOrderError = action.payload || action.error?.message;
      });
  },
});

export const { clearBondPredictErrors, clearBondPredictCurrentMarket, clearBondPredictPortfolio, resetBondPredictPlaceOrder } =
  bondPredictSlice.actions;
export default bondPredictSlice.reducer;

export const selectBondPredictMarkets = (s) => s.bondPredict?.markets || [];
export const selectBondPredictTrending = (s) => s.bondPredict?.trending || [];
export const selectBondPredictLeagues = (s) => s.bondPredict?.leagues || [];
export const selectBondPredictPagination = (s) => s.bondPredict?.pagination;
export const selectBondPredictMarketsStatus = (s) => s.bondPredict?.marketsStatus;
export const selectBondPredictTrendingStatus = (s) => s.bondPredict?.trendingStatus;
export const selectBondPredictMarketsError = (s) => s.bondPredict?.marketsError;
export const selectBondPredictLikeBusyId = (s) => s.bondPredict?.likeBusyId;
export const selectBondPredictCurrentMarket = (s) => s.bondPredict?.currentMarket;
export const selectBondPredictCurrentMarketStatus = (s) =>
  s.bondPredict?.currentMarketStatus;
export const selectBondPredictCurrentMarketError = (s) =>
  s.bondPredict?.currentMarketError;
export const selectBondPredictOrderBook = (s) => s.bondPredict?.orderBook;
export const selectBondPredictMarketTrades = (s) => s.bondPredict?.marketTrades || [];
export const selectBondPredictMarketTradesKey = (s) => s.bondPredict?.marketTradesKey;
export const selectBondPredictMarketTradesStatus = (s) =>
  s.bondPredict?.marketTradesStatus;
export const selectBondPredictOrderBookStatus = (s) => s.bondPredict?.orderBookStatus;
export const selectBondPredictOrderBookKey = (s) => s.bondPredict?.orderBookKey;
export const selectBondPredictMarketStats = (s) => s.bondPredict?.marketStats;
export const selectBondPredictMarketStatsStatus = (s) => s.bondPredict?.marketStatsStatus;
export const selectBondPredictRelatedMarkets = (s) => s.bondPredict?.relatedMarkets || [];
export const selectBondPredictRelatedMarketsStatus = (s) =>
  s.bondPredict?.relatedMarketsStatus;
export const selectBondPredictPortfolioSummary = (s) => s.bondPredict?.portfolioSummary;
export const selectBondPredictPortfolioPositions = (s) =>
  s.bondPredict?.portfolioPositions || [];
export const selectBondPredictPortfolioPagination = (s) => s.bondPredict?.portfolioPagination;
export const selectBondPredictPortfolioStatus = (s) => s.bondPredict?.portfolioStatus;
export const selectBondPredictPortfolioError = (s) => s.bondPredict?.portfolioError;
export const selectBondPredictPortfolioOrders = (s) => s.bondPredict?.portfolioOrders || [];
export const selectBondPredictPortfolioOrdersPagination = (s) =>
  s.bondPredict?.portfolioOrdersPagination;
export const selectBondPredictPortfolioOrdersStatus = (s) =>
  s.bondPredict?.portfolioOrdersStatus;
export const selectBondPredictPortfolioOrdersError = (s) =>
  s.bondPredict?.portfolioOrdersError;
export const selectBondPredictCancelOrderBusyId = (s) => s.bondPredict?.cancelOrderBusyId;
export const selectBondPredictPlaceOrderStatus = (s) => s.bondPredict?.placeOrderStatus;
export const selectBondPredictPlaceOrderError = (s) => s.bondPredict?.placeOrderError;
